const additionalAlertTroubleshootingContext = `
# Investigate this Alert by collecting all relevant information through available data sources:

1. Correlate it with other Alerts occurring at similar times
2. Collect all relevant data from available and related data sources for 1 hour before the Alert (and 1 hour after if it is cleared). Collect data for the current time frame when necessary.
3. Focus on identifying the potential root cause of the issue
4. Perform impact analysis:
   - How does this Alert affect the rest of the network?
   - What is the impact on traffic and performance?
   - Are there any alternative traffic routes in use or backup devices and/or links?
5. Perform any other necessary troubleshooting steps, including asking the user for additional information or clarifications if needed
6. Provide a brief summary of findings at the end and ask users if they have any guidance on additional steps to perform
`;

const KAIZEN_EVENT_TYPES = {
  AGENT_INITIALIZED: 'agent_initialized',
  CHAT_SESSION_UPDATED: 'chat_session_updated',
  AGENT_HEARTBEAT: 'agent_heartbeat',
  AGENT_TURN_STARTED: 'agent_turn_started',
  AGENT_STEP_STARTED: 'agent_step_started',
  AGENT_STEP_UPDATED: 'agent_step_updated',
  // not persisted: the LLM response for the current step, relayed while it generates
  AGENT_STEP_STREAMING: 'agent_step_streaming',
  TOOL_CALL_STARTED: 'tool_call_started',
  TOOL_CALL_SUCCEEDED: 'tool_call_succeeded',
  TOOL_CALL_FAILED: 'tool_call_failed',
  TOOL_CALL_AWAITING_APPROVAL: 'tool_call_awaiting_approval',
  TOOL_CALL_APPROVED: 'tool_call_approved',
  TOOL_CALL_REJECTED: 'tool_call_rejected',
  AGENT_STEP_COMPLETED: 'agent_step_completed',
  AGENT_TURN_COMPLETED: 'agent_turn_completed',
  AGENT_TURN_FAILED: 'agent_turn_failed',
  AGENT_TURN_RESUMED: 'agent_turn_resumed',
  CHAT_MESSAGE_CREATED: 'chat_message_created',
  DISABLED_TOOLS_INFO: 'disabled_tools_info'
};

const PENDING = 'PENDING';
const PROCESSING = 'PROCESSING';
const COMPLETED = 'COMPLETED';
const FAILED = 'FAILED';
const AWAITING_APPROVAL = 'AWAITING_APPROVAL';
const CANCELLED = 'CANCELLED';
const EXPIRED = 'EXPIRED';

const AGENT_TURN_STATUS_TYPES = {
  PENDING,
  PROCESSING,
  COMPLETED,
  FAILED,
  AWAITING_APPROVAL,
  CANCELLED,
  EXPIRED
};

// Marks a turn, and the step it was on, as stopped by the user. The turn is otherwise COMPLETED.
const AGENT_TURN_STOPPED_MESSAGE = 'STOPPED_BY_USER';

const AGENT_TURN_STATUS_ACTIVE = [PENDING, PROCESSING, AWAITING_APPROVAL];
const AGENT_TURN_STATUS_DONE = [COMPLETED, FAILED, CANCELLED, EXPIRED];
const AGENT_TURN_STATUS = [...AGENT_TURN_STATUS_ACTIVE, ...AGENT_TURN_STATUS_DONE];

const TOOL_CALL_STATUS = {
  PENDING: 'PENDING',
  SUCCESS: 'SUCCESS',
  FAILED: 'FAILED',
  TIMED_OUT: 'TIMED_OUT',
  AWAITING_APPROVAL: 'AWAITING_APPROVAL',
  USER_APPROVED: 'USER_APPROVED',
  USER_REJECTED: 'USER_REJECTED',
  APPROVAL_TIMEOUT: 'APPROVAL_TIMEOUT',
  EXECUTING: 'EXECUTING',
  CANCELLED: 'CANCELLED'
};

const TOOL_PAUSE_KIND = /** @type {const} */ ({
  CONNECT: 'connect',
  SERVER_APPROVAL: 'server_approval'
});

// Pauses on an MCP server: its rows of a step are decided together and block the server when declined.
const SERVER_PAUSE_KINDS = [TOOL_PAUSE_KIND.CONNECT, TOOL_PAUSE_KIND.SERVER_APPROVAL];

// Rows a paused step still owes a result for.
const TOOL_CALL_UNFINISHED_STATUSES = [
  TOOL_CALL_STATUS.AWAITING_APPROVAL,
  TOOL_CALL_STATUS.USER_APPROVED,
  TOOL_CALL_STATUS.EXECUTING
];

const TOOL_DECISION_REFUSAL = /** @type {const} */ ({
  NOT_CONNECTED: 'not_connected',
  UNAVAILABLE: 'unavailable',
  NOT_ASKER: 'not_asker'
});

// Why a call to a server was answered without running it.
const TOOL_NOT_RUN_REASON = /** @type {const} */ ({
  SKIPPED: 'skipped',
  ACCESS_LOST: 'access_lost',
  UNAVAILABLE: 'unavailable',
  CHANGED: 'changed',
  NO_LONGER_AVAILABLE: 'no_longer_available',
  NO_TOOLS: 'no_tools'
});

const MCP_CONNECT_WAIT_SECONDS = 1800;

// A FAILED turn carrying this error_message was cut off by a restart, not by an error.
const TURN_INTERRUPTED_TEXT = 'This answer was interrupted. Ask again to continue.';

// The error_message of a call its answer's interruption closed; the chat shows it in place of an error.
const TOOL_INTERRUPTED_TEXT = /** @type {const} */ ({
  MAY_HAVE_RUN: 'This call may have run before an interruption. Its result is unknown, and it was not run again.',
  NOT_RUN: 'This request was interrupted, so this call did not run.'
});

// Session feature keys — static enum
const SESSION_FEATURE_KEYS = {
  SYNTHETICS_SUBAGENT: 'synthetics_subagent',
  ENABLE_UDE_QUERY: 'enable_ude_query',
  PEERINGDB_TOOL: 'peeringdb_tool'
};

// Feature metadata for UI rendering and validation
const SESSION_FEATURES = [
  {
    key: SESSION_FEATURE_KEYS.SYNTHETICS_SUBAGENT,
    label: 'Enable Synthetics Agent',
    description: 'Use a dedicated sub-agent for synthetics monitoring questions',
    requiresCapability: 'hasSyntheticTests'
  },
  {
    key: SESSION_FEATURE_KEYS.PEERINGDB_TOOL,
    label: 'Enable PeeringDB Tool',
    description:
      'Use the PeeringDB (exchanges, facilities, ASNs) tool directly on the main agent instead of the dedicated PeeringDB sub-agent'
  },
  {
    key: SESSION_FEATURE_KEYS.ENABLE_UDE_QUERY,
    label: 'Enable UDE Tools',
    description: 'Use UDE query tools instead of legacy Data Explorer and NMS tools',
    requiresCapability: 'ude'
  }
];

function isMcpTool(toolName) {
  return typeof toolName === 'string' && toolName.startsWith('mcp_');
}

const MCP_OAUTH_BROADCAST_CHANNEL = 'mcp-oauth-callback';
const MCP_OAUTH_CALLBACK_PATH = '/api/ui/ai/mcp-servers/oauth/callback';

// Headers are resolved independently; this only says whether the server also uses OAuth.
const MCP_AUTH_TYPE = {
  HEADERS: 'headers',
  OAUTH: 'oauth'
};

const MCP_AUTH_TYPES = Object.values(MCP_AUTH_TYPE);

// Enterprise MFA chains need the time; the popup and the stored state expire together.
const MCP_OAUTH_STATE_TTL_SECONDS = 600;

module.exports = {
  additionalAlertTroubleshootingContext,
  isMcpTool,
  KAIZEN_EVENT_TYPES,
  AGENT_TURN_STATUS,
  AGENT_TURN_STATUS_ACTIVE,
  AGENT_TURN_STATUS_DONE,
  AGENT_TURN_STATUS_TYPES,
  AGENT_TURN_STOPPED_MESSAGE,
  TOOL_CALL_STATUS,
  TOOL_CALL_UNFINISHED_STATUSES,
  TOOL_DECISION_REFUSAL,
  TOOL_NOT_RUN_REASON,
  TOOL_PAUSE_KIND,
  SERVER_PAUSE_KINDS,
  MCP_CONNECT_WAIT_SECONDS,
  TURN_INTERRUPTED_TEXT,
  TOOL_INTERRUPTED_TEXT,
  SESSION_FEATURE_KEYS,
  SESSION_FEATURES,
  MCP_OAUTH_BROADCAST_CHANNEL,
  MCP_OAUTH_CALLBACK_PATH,
  MCP_OAUTH_STATE_TTL_SECONDS,
  MCP_AUTH_TYPE,
  MCP_AUTH_TYPES
};
